import { FiBriefcase, FiDollarSign, FiHash, FiPackage, FiGitBranch, FiUserCheck, FiClock } from 'react-icons/fi';

/**
 * Registry of metadata types shown in the Metadata admin area.
 * Add a new entry here (and its API route + table) to expose another kind.
 * Set panel: 'custom' for types with a dedicated React panel.
 */
const ALL_METADATA_TYPES = [
  {
    id: 'requisition-statuses',
    label: 'Requisition statuses',
    singularLabel: 'Requisition status',
    description:
      'Workflow stages for requisitions (Draft → Review → Approval → Approved). Enable Email actions so assignees can Approve/Reject from email. Sort order controls the approve sequence. Approved drafts a Client PO under Revenue → Client POs.',
    icon: FiGitBranch,
    enabled: true,
    apiBase: '/requisition-statuses',
    queryKey: ['requisition-statuses', 'metadata'],
    cacheRootKey: 'requisition-statuses',
    listParams: { include_inactive: 'true' },
    nameField: 'name',
    columns: [
      { key: 'code', label: 'Code', width: 120 },
      { key: 'name', label: 'Name', width: 160 },
      { key: 'sort_order', label: 'Order', width: 80 },
      { key: 'email_actions', label: 'Email actions', width: 110, type: 'bool' },
      { key: 'notify_assignees', label: 'Notify', width: 80, type: 'bool' },
      { key: 'is_active', label: 'Status', width: 100, type: 'status' },
    ],
    formFields: [
      {
        key: 'code',
        label: 'Code',
        type: 'text',
        required: true,
        placeholder: 'e.g., legal_review',
      },
      {
        key: 'name',
        label: 'Name',
        type: 'text',
        required: true,
        placeholder: 'e.g., Legal review',
      },
      {
        key: 'description',
        label: 'Description',
        type: 'textarea',
        placeholder: 'What happens in this stage',
        rows: 2,
      },
      {
        key: 'sort_order',
        label: 'Sort order',
        type: 'number',
        placeholder: '50',
      },
      {
        key: 'notify_assignees',
        label: 'Notify assignees (email + in-app)',
        type: 'checkbox',
        defaultValue: true,
        hint: 'Send email when a requisition enters this stage',
      },
      {
        key: 'email_actions',
        label: 'Email actions (Approve / Reject links)',
        type: 'checkbox',
        defaultValue: false,
        hint: 'Assignees can approve/reject from email without signing in',
      },
      {
        key: 'require_all_approvals',
        label: 'Require all assignees (AND gate)',
        type: 'checkbox',
        defaultValue: false,
        hint: 'Every stage assignee must approve before advancing',
      },
      {
        key: 'is_initial',
        label: 'Initial / drafting stage',
        type: 'checkbox',
      },
      {
        key: 'is_completed',
        label: 'Completed (terminal)',
        type: 'checkbox',
      },
      {
        key: 'is_rejected',
        label: 'Rejected (terminal)',
        type: 'checkbox',
      },
      {
        key: 'is_active',
        label: 'Status',
        type: 'active-select',
      },
    ],
  },
  {
    id: 'buying-statuses',
    label: 'Buying statuses',
    singularLabel: 'Buying status',
    description:
      'Shared workflow stages for vendor POs, quotations, and bills (Draft → Review → Approval → Approved). Enable Email actions so assignees can Approve/Reject from email. Vendor bills post to the ledger only after Approved.',
    icon: FiGitBranch,
    enabled: true,
    apiBase: '/buying-statuses',
    queryKey: ['buying-statuses', 'metadata'],
    cacheRootKey: 'buying-statuses',
    listParams: { include_inactive: 'true' },
    nameField: 'name',
    columns: [
      { key: 'code', label: 'Code', width: 120 },
      { key: 'name', label: 'Name', width: 160 },
      { key: 'sort_order', label: 'Order', width: 80 },
      { key: 'email_actions', label: 'Email actions', width: 110, type: 'bool' },
      { key: 'notify_assignees', label: 'Notify', width: 80, type: 'bool' },
      { key: 'is_active', label: 'Status', width: 100, type: 'status' },
    ],
    formFields: [
      {
        key: 'code',
        label: 'Code',
        type: 'text',
        required: true,
        placeholder: 'e.g., legal_review',
      },
      {
        key: 'name',
        label: 'Name',
        type: 'text',
        required: true,
        placeholder: 'e.g., Legal review',
      },
      {
        key: 'description',
        label: 'Description',
        type: 'textarea',
        placeholder: 'What happens in this stage',
        rows: 2,
      },
      {
        key: 'sort_order',
        label: 'Sort order',
        type: 'number',
        placeholder: '50',
      },
      {
        key: 'notify_assignees',
        label: 'Notify assignees (email + in-app)',
        type: 'checkbox',
        defaultValue: true,
        hint: 'Send email when a vendor document enters this stage',
      },
      {
        key: 'email_actions',
        label: 'Email actions (Approve / Reject links)',
        type: 'checkbox',
        defaultValue: false,
        hint: 'Assignees can approve/reject from email without signing in',
      },
      {
        key: 'require_all_approvals',
        label: 'Require all assignees (AND gate)',
        type: 'checkbox',
        defaultValue: false,
        hint: 'Every stage assignee must approve before advancing',
      },
      {
        key: 'is_initial',
        label: 'Initial / drafting stage',
        type: 'checkbox',
      },
      {
        key: 'is_completed',
        label: 'Completed (terminal)',
        type: 'checkbox',
      },
      {
        key: 'is_rejected',
        label: 'Rejected (terminal)',
        type: 'checkbox',
      },
      {
        key: 'is_active',
        label: 'Status',
        type: 'active-select',
      },
    ],
  },
  {
    id: 'register-statuses',
    label: 'Register onboarding',
    singularLabel: 'Register stage',
    description:
      'Onboarding pipeline for register intake: Sign Up → Welcome Email → Questionnaire → Questionnaire completed → Candidate. Labels and order are editable; operational hold/disabled statuses may also appear here.',
    icon: FiUserCheck,
    enabled: true,
    apiBase: '/register-statuses',
    queryKey: ['register-statuses', 'metadata'],
    cacheRootKey: 'register-statuses',
    listParams: { include_inactive: 'true' },
    nameField: 'name',
    columns: [
      { key: 'code', label: 'Code', width: 140 },
      { key: 'name', label: 'Name', width: 180 },
      { key: 'step_type', label: 'Step type', width: 140 },
      { key: 'sort_order', label: 'Order', width: 80 },
      { key: 'is_active', label: 'Status', width: 100, type: 'status' },
    ],
    formFields: [
      {
        key: 'code',
        label: 'Code',
        type: 'text',
        required: true,
        placeholder: 'e.g., questionnaire',
      },
      {
        key: 'name',
        label: 'Name',
        type: 'text',
        required: true,
        placeholder: 'e.g., Questionnaire',
      },
      {
        key: 'description',
        label: 'Description',
        type: 'textarea',
        rows: 2,
      },
      {
        key: 'step_type',
        label: 'Step type',
        type: 'text',
        placeholder: 'signup | welcome | questionnaire | questionnaire_done | candidate',
        hint: 'Maps stage to onboarding progress logic',
      },
      {
        key: 'sort_order',
        label: 'Sort order',
        type: 'number',
        placeholder: '30',
      },
      { key: 'is_initial', label: 'Initial stage', type: 'checkbox' },
      { key: 'is_completed', label: 'Completed (terminal)', type: 'checkbox' },
      { key: 'is_rejected', label: 'Rejected / disabled', type: 'checkbox' },
      { key: 'is_hold', label: 'Hold (future follow-up)', type: 'checkbox' },
      { key: 'is_active', label: 'Status', type: 'active-select' },
    ],
  },
  {
    id: 'workflow-roles',
    label: 'Workflow roles',
    singularLabel: 'Workflow role',
    description:
      'Roles for customer and vendor contacts plus internal approvers in Req / PO / buying workflows (Reviewer, Approver, Vendor approver, Signatory). These are not app login roles.',
    icon: FiUserCheck,
    enabled: true,
    apiBase: '/workflow-roles',
    queryKey: ['workflow-roles', 'metadata'],
    cacheRootKey: 'workflow-roles',
    listParams: { include_inactive: 'true' },
    nameField: 'name',
    columns: [
      { key: 'code', label: 'Code', width: 140 },
      { key: 'name', label: 'Name', width: 160 },
      { key: 'sort_order', label: 'Order', width: 80 },
      { key: 'description', label: 'Description', width: 280 },
      { key: 'is_active', label: 'Status', width: 100, type: 'status' },
    ],
    formFields: [
      {
        key: 'code',
        label: 'Code',
        type: 'text',
        required: true,
        placeholder: 'e.g., reviewer',
      },
      {
        key: 'name',
        label: 'Name',
        type: 'text',
        required: true,
        placeholder: 'e.g., Reviewer',
      },
      {
        key: 'description',
        label: 'Description',
        type: 'textarea',
        placeholder: 'How this role is used in the workflow',
        rows: 2,
      },
      {
        key: 'sort_order',
        label: 'Sort order',
        type: 'number',
        placeholder: '100',
      },
      {
        key: 'is_active',
        label: 'Status',
        type: 'active-select',
      },
    ],
  },
  {
    id: 'job-roles',
    label: 'Job Roles',
    singularLabel: 'Job Role',
    description:
      'Standard job roles used as classifications in candidate profiles and job postings.',
    icon: FiBriefcase,
    enabled: true,
    apiBase: '/job-roles',
    queryKey: ['job-roles', 'metadata'],
    cacheRootKey: 'job-roles',
    listParams: { include_inactive: 'true' },
    nameField: 'name',
    columns: [
      { key: 'name', label: 'Name', width: 200 },
      { key: 'description', label: 'Description', width: 300 },
      { key: 'is_active', label: 'Status', width: 100, type: 'status' },
    ],
    formFields: [
      {
        key: 'name',
        label: 'Name',
        type: 'text',
        required: true,
        placeholder: 'e.g., Software Engineer',
      },
      {
        key: 'description',
        label: 'Description',
        type: 'textarea',
        placeholder: 'Optional description for this job role',
        rows: 3,
      },
      {
        key: 'is_active',
        label: 'Status',
        type: 'active-select',
      },
    ],
  },
  {
    id: 'service-catalog-codes',
    label: 'Service Codes',
    singularLabel: 'Service Code',
    description:
      'Catalog codes used when building service packages (interview, resume_edit, etc.).',
    icon: FiHash,
    enabled: true,
    apiBase: '/service-catalog-codes',
    queryKey: ['service-catalog-codes', 'metadata'],
    cacheRootKey: 'service-catalog-codes',
    listParams: { include_inactive: 'true' },
    nameField: 'name',
    columns: [
      { key: 'code', label: 'Code', width: 160 },
      { key: 'name', label: 'Name', width: 200 },
      { key: 'description', label: 'Description', width: 280 },
      { key: 'is_active', label: 'Status', width: 100, type: 'status' },
    ],
    formFields: [
      {
        key: 'code',
        label: 'Code',
        type: 'text',
        required: true,
        placeholder: 'e.g., interview',
      },
      {
        key: 'name',
        label: 'Name',
        type: 'text',
        required: true,
        placeholder: 'e.g., Mock interview',
      },
      {
        key: 'description',
        label: 'Description',
        type: 'textarea',
        placeholder: 'Optional description',
        rows: 3,
      },
      {
        key: 'is_active',
        label: 'Status',
        type: 'active-select',
      },
    ],
  },
  {
    id: 'service-catalog-units',
    label: 'Service Units',
    singularLabel: 'Service Unit',
    description: 'Units of measure for package entitlements (session, minute, hour, etc.).',
    icon: FiPackage,
    enabled: true,
    apiBase: '/service-catalog-units',
    queryKey: ['service-catalog-units', 'metadata'],
    cacheRootKey: 'service-catalog-units',
    listParams: { include_inactive: 'true' },
    nameField: 'name',
    columns: [
      { key: 'code', label: 'Code', width: 140 },
      { key: 'name', label: 'Name', width: 180 },
      { key: 'description', label: 'Description', width: 280 },
      { key: 'is_active', label: 'Status', width: 100, type: 'status' },
    ],
    formFields: [
      {
        key: 'code',
        label: 'Code',
        type: 'text',
        required: true,
        placeholder: 'e.g., session',
      },
      {
        key: 'name',
        label: 'Name',
        type: 'text',
        required: true,
        placeholder: 'e.g., Session',
      },
      {
        key: 'description',
        label: 'Description',
        type: 'textarea',
        placeholder: 'Optional description',
        rows: 3,
      },
      {
        key: 'is_active',
        label: 'Status',
        type: 'active-select',
      },
    ],
  },
  {
    id: 'currencies',
    label: 'Currency',
    singularLabel: 'Currency',
    description:
      'Country, currency codes, conversion rates, and related FX details pulled from the configured provider. Sync from Settings overwrites this table.',
    icon: FiDollarSign,
    enabled: true,
    panel: 'currencies',
  },
  {
    id: 'support-sla',
    label: 'Support SLA',
    singularLabel: 'Support SLA',
    description:
      'Resolution SLA targets by ticket priority (Urgent / High / Normal / Low). Support shows a live clock and marks tickets Overdue when the target is missed.',
    icon: FiClock,
    enabled: true,
    panel: 'support-sla',
  },
];

const GODASH_METADATA_IDS = new Set(['job-roles', 'register-statuses', 'support-sla']);

export const METADATA_TYPES = ALL_METADATA_TYPES.filter((t) => GODASH_METADATA_IDS.has(t.id));

export const DEFAULT_METADATA_TYPE_ID =
  METADATA_TYPES.find((t) => t.enabled)?.id || METADATA_TYPES[0]?.id;

export function getMetadataType(typeId) {
  return METADATA_TYPES.find((t) => t.id === typeId && t.enabled);
}

export function getEnabledMetadataTypes() {
  return METADATA_TYPES.filter((t) => t.enabled);
}
